import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Form } from '@/components/ui/form';
import { displayToastSuccess } from '@/helpers/display-toast';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { SingleSelect } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { useDeleteRole } from '@/modules/settings/hooks/roles/use-delete-roles';
import { Role, RoleFormData } from '@/modules/settings/types/role';

interface DeleteRoleModalProps {
  role: RoleFormData | null;
  allRoles: Role[];
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

// Special value for "No role" option
const NO_ROLE_VALUE = -1;

// Dynamic schema based on whether role has members
// Note: SingleSelect returns string values, so we accept string and transform to number
const createDeleteSchema = (hasMembers: boolean) =>
  z.object({
    replacementRoleId: hasMembers
      ? z
          .string({
            required_error: 'authenticated.settings:pages.roles.deleteModal.replacementRequired',
          })
          .transform(val => parseInt(val, 10))
      : z
          .string()
          .optional()
          .transform(val => (val ? parseInt(val, 10) : undefined)),
  });

type DeleteFormData = z.infer<ReturnType<typeof createDeleteSchema>>;

/**
 * Modal for deleting a role with optional member transfer
 * If role has members, user must select a replacement role
 *
 * @example
 * ```tsx
 * <DeleteRoleModal
 *   role={selectedRole}
 *   allRoles={roles}
 *   open={isModalOpen}
 *   onClose={() => setIsModalOpen(false)}
 *   onSuccess={() => {
 *     toast.success('Role deleted');
 *     setIsModalOpen(false);
 *   }}
 * />
 * ```
 */
export function DeleteRoleModal({
  role,
  allRoles,
  open,
  onClose,
  onSuccess,
}: DeleteRoleModalProps) {
  const { t } = useTranslation('authenticated.settings');
  const deleteRoleMutation = useDeleteRole();

  const hasMembers = role ? role.memberCount > 0 : false;

  const form = useForm<DeleteFormData>({
    resolver: zodResolver(createDeleteSchema(hasMembers)),
    mode: 'onChange',
    defaultValues: {
      replacementRoleId: undefined,
    },
  });

  // Build replacement options with "No role" as first option
  const replacementOptions = [
    {
      value: NO_ROLE_VALUE.toString(),
      label: t('pages.roles.deleteModal.noRole'),
    },
    ...allRoles
      .filter(r => r.id !== role?.roleId)
      .map(r => ({
        value: r.id.toString(),
        label: r.name,
      })),
  ];

  const handleDelete = async (closeModal: () => void) => {
    if (!role) return;

    const isValid = await form.trigger();
    if (!isValid) return;

    const formData = form.getValues();
    const roleName = role.name;

    // Build request payload conditionally
    const payload: {
      roleId: number;
      replacementRoleId?: number;
    } = {
      roleId: role.roleId,
    };

    // form.getValues() returns the raw string value (pre-transform)
    // So we compare against the string representation of NO_ROLE_VALUE
    const rawValue = formData.replacementRoleId;
    if (rawValue !== undefined && rawValue !== null) {
      const stringValue = String(rawValue);
      if (stringValue !== NO_ROLE_VALUE.toString()) {
        payload.replacementRoleId = parseInt(stringValue, 10);
      }
    }

    try {
      await deleteRoleMutation.mutateAsync(payload);
      form.reset();
      closeModal();
      displayToastSuccess(
        t('pages.roles.deleteModal.successDescription', {
          name: roleName,
        })
      );
      onSuccess?.();
    } catch {
      // Error handled by mutation onError
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const replacementRoleId = form.watch('replacementRoleId');
  const isFormValid = !hasMembers || !!replacementRoleId;

  const messageContent = hasMembers ? (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        <Trans
          i18nKey="authenticated.settings:pages.roles.deleteModal.messageWithMembers"
          values={{ count: role?.memberCount }}
          components={{ strong: <strong className="font-semibold text-foreground" /> }}
        />
      </div>
    </div>
  ) : (
    <div className="text-sm text-muted-foreground">
      {t('pages.roles.deleteModal.messageNoMembers')}
    </div>
  );

  return (
    <ConfirmationModal
      open={open}
      onOpen={() => {}}
      onClose={handleClose}
      title={t('pages.roles.deleteModal.title')}
      message={messageContent}
      confirmText={
        deleteRoleMutation.isPending
          ? t('pages.roles.deleteModal.deleting')
          : t('pages.roles.deleteModal.delete')
      }
      confirmVariant="destructive"
      onConfirm={handleDelete}
      isLoading={deleteRoleMutation.isPending}
      disabled={!isFormValid}
    >
      {hasMembers && (
        <Form {...form}>
          <form className="space-y-4">
            <SingleSelect
              name="replacementRoleId"
              control={form.control}
              options={replacementOptions}
              label={t('pages.roles.deleteModal.replacementLabel')}
              placeholder={t('pages.roles.deleteModal.replacementPlaceholder')}
              searchable={true}
              isClearSelectionEnabled={true}
            />
          </form>
        </Form>
      )}
    </ConfirmationModal>
  );
}
